import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore as getFullFirestore } from "firebase/firestore";
import type { Firestore as LiteFirestore } from "firebase/firestore/lite";
import { getFirestore } from "firebase/firestore/lite";
import { getFunctions } from "firebase/functions";
import { getPerformance } from "firebase/performance";
import { getStorage } from "firebase/storage";
import {
  ALL_REGIONS,
  buildRegionalFunctionName,
  type DataRegion,
  REGION_CONFIG,
} from "../regionConfig";

export type { DataRegion } from "../regionConfig";

// Get config from URL (if present), else cookie/localStorage in development, else env vars
const getFirebaseConfig = () => {
  // 1) Highest precedence: URL query params (works across ports if passed)
  if (typeof window !== "undefined" && typeof URLSearchParams !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const urlApiKey = params.get("firebaseApiKey");
    const urlAuthDomain = params.get("firebaseAuthDomain");
    const urlProjectId = params.get("firebaseProjectId");
    const urlStorageBucket = params.get("firebaseStorageBucket");
    const urlMessagingSenderId = params.get("firebaseMessagingSenderId");
    const urlAppId = params.get("firebaseAppId");
    const urlDatabaseURL = params.get("firebaseDatabaseURL");

    const hasAllRequired = Boolean(
      urlApiKey &&
        urlAuthDomain &&
        urlProjectId &&
        urlStorageBucket &&
        urlMessagingSenderId &&
        urlAppId,
    );

    if (
      hasAllRequired &&
      urlApiKey &&
      urlAuthDomain &&
      urlProjectId &&
      urlStorageBucket &&
      urlMessagingSenderId &&
      urlAppId
    ) {
      return {
        apiKey: urlApiKey,
        authDomain: urlAuthDomain,
        databaseURL: urlDatabaseURL || undefined,
        projectId: urlProjectId,
        storageBucket: urlStorageBucket,
        messagingSenderId: urlMessagingSenderId,
        appId: urlAppId,
      };
    }
  }

  // 2) In non-production, allow .env test override via localStorage flag
  if (import.meta.env.VITE_APP_MODE !== "production") {
    const isTestDbFlag =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("test-db") === "true";
    if (isTestDbFlag) {
      return {
        apiKey:
          import.meta.env.VITE_TEST_FIREBASE_API_KEY ||
          "AIzaSyBwYr_3am0JX3-o1Mn9_P3p5ALQvh822HM",
        authDomain:
          import.meta.env.VITE_TEST_FIREBASE_AUTH_DOMAIN ||
          "senf-try.firebaseapp.com",
        projectId: import.meta.env.VITE_TEST_FIREBASE_PROJECT_ID || "senf-try",
        storageBucket:
          import.meta.env.VITE_TEST_FIREBASE_STORAGE_BUCKET ||
          "senf-try.appspot.com",
        messagingSenderId:
          import.meta.env.VITE_TEST_FIREBASE_MESSAGING_SENDER_ID ||
          "671029817209",
        appId:
          import.meta.env.VITE_TEST_FIREBASE_APP_ID ||
          "1:671029817209:web:ce3145807442727c2d09a8",
      };
    }
  }

  // 3) In dev, allow cookie/localStorage override (legacy)
  if (import.meta.env.VITE_APP_MODE === "development") {
    // Prefer cookie so it works across localhost ports (3000, 3001, ...)
    const cookieString = typeof document !== "undefined" ? document.cookie : "";
    const cookieMatch = cookieString
      ?.split("; ")
      .find((row) => row.startsWith("firebaseConfig="));
    if (cookieMatch) {
      try {
        const cookieValue = decodeURIComponent(cookieMatch.split("=")[1] || "");
        if (cookieValue) return JSON.parse(cookieValue);
      } catch {
        // ignore malformed cookie and fall back to localStorage/env
      }
    }

    const sessionConfig =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("firebaseConfig")
        : null;
    if (sessionConfig) {
      try {
        return JSON.parse(sessionConfig);
      } catch {
        // ignore and fall through to env vars
      }
    }
  }

  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
};

const firebaseConfig = getFirebaseConfig();

if (!firebaseConfig.projectId) {
  throw new Error(
    "[firebase] projectId is missing from the Firebase config. " +
      "Ensure VITE_FIREBASE_PROJECTID is set in your .env file.",
  );
}

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
/**
 * Firebase Performance Monitoring singleton for the current app.
 *
 * This runs once for the whole Firebase app, not for each Firestore database.
 * Page loads and network requests are tracked automatically after setup.
 * Use `firebasePerformance` directly only when you want custom traces.
 *
 * @example
 * ```ts
 * import { trace } from "firebase/performance";
 * import { firebasePerformance } from "senf-shared";
 *
 * const t = trace(firebasePerformance, "save_project");
 * t.start();
 *
 * try {
 *   await saveProject();
 * } finally {
 *   t.stop();
 * }
 * ```
 */
const firebasePerformance = getPerformance(firebaseApp);

// ---------------------------------------------------------------------------
// Regional Firestore / Storage / Functions instances — built dynamically from REGION_CONFIG
// ---------------------------------------------------------------------------
type RegionInstances = {
  db: LiteFirestore;
  fullDb: ReturnType<typeof getFullFirestore>;
  storage: ReturnType<typeof getStorage>;
  cloudFunctions: ReturnType<typeof getFunctions>;
};

const partial: Partial<Record<DataRegion, RegionInstances>> = {};

for (const region of ALL_REGIONS) {
  const { databaseId, storageBucketSuffix, computeRegion } =
    REGION_CONFIG[region];
  const isDefault = databaseId === "(default)";

  partial[region] = {
    db: isDefault
      ? getFirestore(firebaseApp)
      : getFirestore(firebaseApp, databaseId),
    fullDb: isDefault
      ? getFullFirestore(firebaseApp)
      : getFullFirestore(firebaseApp, databaseId),
    storage: storageBucketSuffix
      ? getStorage(
          firebaseApp,
          `gs://${firebaseConfig.projectId}${storageBucketSuffix}`,
        )
      : getStorage(firebaseApp),
    cloudFunctions: getFunctions(firebaseApp, computeRegion),
  };
}

const regionInstances = partial as Record<DataRegion, RegionInstances>;

// Active region — switched by setDataRegion(), defaults to EU.
export let db = regionInstances.eu.db;
export let fullDb = regionInstances.eu.fullDb;
export let storage = regionInstances.eu.storage;
export let cloudFunctions = regionInstances.eu.cloudFunctions;

let currentRegion: DataRegion = "eu";
let regionReady = false;
const regionSubscribers = new Set<() => void>();

/** Subscribe to region changes — used by RegionBoundary via useSyncExternalStore. */
export function subscribeToRegion(cb: () => void) {
  regionSubscribers.add(cb);
  return () => {
    regionSubscribers.delete(cb);
  };
}

/** Returns the current active region key. */
export function getCurrentRegion(): DataRegion {
  return currentRegion;
}

/** True once setDataRegion() has been called at least once. Used by RegionBoundary to block rendering until the correct db is active. */
export function getRegionReady(): boolean {
  return regionReady;
}

/**
 * Stable snapshot for useSyncExternalStore that encodes both region and ready state.
 * Returns "" when not ready (falsy → render nothing), or the region key when ready.
 * Avoids creating new objects on every call which would break Object.is comparison.
 */
export function getRegionSnapshot(): string {
  return regionReady ? currentRegion : "";
}

/**
 * Full URL for a Cloud Function, routed to the correct compute region.
 * EU keeps the legacy unsuffixed name; other regions get an uppercase suffix
 * (e.g. "tagSurveyEntriesUSA"). Defaults to the currently active region.
 */
export function getCloudFunctionUrl(
  baseName: string,
  dataRegion?: DataRegion,
): string {
  const region = dataRegion ?? currentRegion;
  const { computeRegion } = REGION_CONFIG[region];
  const functionName = buildRegionalFunctionName(baseName, region);
  return `https://${computeRegion}-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;
}

// Persisted cache keys that hold regional Firestore/Storage data and would
// leak EU data into a US-routed session (or vice versa) if not cleared on
// region flip. All entries are caches, never source-of-truth, so dropping
// them is safe — modules refetch on next access.
//
// Two match strategies based on key shape:
//   - PREFIXES: cleared if `key.startsWith(prefix)`. Use for caches whose
//     real keys carry a dynamic suffix (e.g. surveyId, projectId, version),
//     so a single prefix sweeps every variant — including future
//     CACHE_VERSION bumps in the owning module.
//   - EXACT KEYS: cleared if `key === entry`. Use for caches with a single
//     fixed key.
//
// In-memory module caches (e.g. surveyAggregatesCache, rawCache) do NOT
// belong here — those use `getCurrentRegion()` in their internal cache key,
// so old entries become unreachable after a flip without explicit clearing.
const REGION_SCOPED_LOCAL_PREFIXES = [
  // useSurveyDataManager: surveyDataCache_<CACHE_VERSION>_<surveyId>
  "surveyDataCache_",
  // useParticipationCounts: senf:admin:part:project:<projectId>
  "senf:admin:part:",
];

const REGION_SCOPED_LOCAL_KEYS = [
  // SenfGpt chat history — single key
  "insights_chatHistory",
];

const REGION_SCOPED_SESSION_KEYS = [
  // SurveyFinder per-survey entry counts
  "senf:admin:surveyFinder:entryCounts",
  // MediaBrowser file/thumbnail caches keyed by storage path
  "senf-media-browser-file-cache",
  "senf-media-browser-thumbnail-cache",
];

function clearRegionScopedCaches() {
  try {
    if (typeof localStorage !== "undefined") {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (REGION_SCOPED_LOCAL_KEYS.includes(key)) {
          toRemove.push(key);
          continue;
        }
        for (const prefix of REGION_SCOPED_LOCAL_PREFIXES) {
          if (key.startsWith(prefix)) {
            toRemove.push(key);
            break;
          }
        }
      }
      for (const key of toRemove) localStorage.removeItem(key);
    }
  } catch {}

  try {
    if (typeof sessionStorage !== "undefined") {
      for (const key of REGION_SCOPED_SESSION_KEYS) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {}
}

/** Switch the active db/storage/cloudFunctions to the given region. No-op if already on that region and already ready. */
export function setDataRegion(region: DataRegion | undefined) {
  const resolved = region && region in REGION_CONFIG ? region : "eu";
  const wasReady = regionReady;
  regionReady = true;

  const regionChanged = resolved !== currentRegion;
  if (!regionChanged && wasReady) return;

  if (regionChanged) {
    const prev = currentRegion;
    currentRegion = resolved;
    db = regionInstances[resolved].db;
    fullDb = regionInstances[resolved].fullDb;
    storage = regionInstances[resolved].storage;
    cloudFunctions = regionInstances[resolved].cloudFunctions;
    try {
      localStorage.setItem("senf_region", resolved);
    } catch {}
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      console.log(`[Region] ${prev} -> ${resolved}`);
    }

    // Drop region-scoped caches BEFORE notifying subscribers so subscribers
    // that read storage during their refresh don't see stale entries.
    if (wasReady) clearRegionScopedCaches();
  }

  for (const cb of regionSubscribers) cb();
}

// ---------------------------------------------------------------------------
// Multi-region arrays — iterate these instead of hardcoding [euDb, usaDb]
// Adding a new region to REGION_CONFIG auto-extends these arrays.
// ---------------------------------------------------------------------------

/** All lite Firestore instances, one per region. */
export const allDbs = ALL_REGIONS.map((region) => regionInstances[region].db);

/** All full Firestore instances, one per region. */
export const allFullDbs = ALL_REGIONS.map(
  (region) => regionInstances[region].fullDb,
);

/** Tagged array: each entry has the region key + db instance.
 *  Use when you need to know WHICH region a result came from. */
export const regionDbs = ALL_REGIONS.map((region) => ({
  region,
  db: regionInstances[region].db,
}));

// ---------------------------------------------------------------------------
// Pre-signout teardown registry
// Call runPreSignOutCallbacks() before Firebase signOut so that active
// Firestore onSnapshot listeners are dropped BEFORE the auth token is
// revoked — preventing "Missing or insufficient permissions" errors.
// ---------------------------------------------------------------------------
const preSignOutCallbacks = new Set<() => void>();

/** Register a callback to run before every signOut. Returns a deregister fn. */
export function registerPreSignOutCallback(cb: () => void): () => void {
  preSignOutCallbacks.add(cb);
  return () => preSignOutCallbacks.delete(cb);
}

/** Run all registered pre-signout callbacks. Called by AuthProvider before signOut. */
export function runPreSignOutCallbacks(): void {
  for (const cb of preSignOutCallbacks) {
    try {
      cb();
    } catch (err) {
      console.error("[Pre-signout callback error]", err);
    }
  }
}

export default firebaseApp;
export { auth, firebaseConfig, firebasePerformance };
